import * as React from "react";
import { css } from "@emotion/core";
import { Button, Callout, FormGroup, TextArea } from "@blueprintjs/core";
import ApiOrders from "api/orders";
import { useSinkState, useSinkActions } from "context/sink";
import {
  useUploadMachine,
  useUploadMachineSelector,
} from "../../context/upload-machine";
import { generateUniqueFileIdentifier } from "../../state/uploadMachine";
import { LidarMachineContext } from "../../state/lidarMachine";
import { ImagesMachineContext } from "../../state/imagesMachine";
import { GeometryMachineContext } from "../../state/geometryMachine";
import { OrderMachineContext } from "../../state/orderMachine";
import { validateOrder, ValidationResult } from "../../utils/validation";
import WarningDialog from "../WarningDialog";

interface UploadActionProps {
  fileAgeMap: Map<string, boolean>;
}

export default function UploadAction({ fileAgeMap }: UploadActionProps) {
  const [showWarningDialog, setShowWarningDialog] = React.useState(false);

  const { sink, selectedSurveyId } = useSinkState();

  const uploadMachine = useUploadMachine();
  const orderMachine = OrderMachineContext.useActorRef();

  const lidarUploads = LidarMachineContext.useSelector(
    (state) => state.context.uploads
  );

  const imageUploads = ImagesMachineContext.useSelector(
    (state) => state.context.uploads
  );

  const geometryUploads = GeometryMachineContext.useSelector(
    (state) => state.context.uploads
  );

  const timezone = OrderMachineContext.useSelector(
    (state) => state.context.timezone
  );
  const latitude = OrderMachineContext.useSelector(
    (state) => state.context.center?.[1] ?? "0"
  );
  const longitude = OrderMachineContext.useSelector(
    (state) => state.context.center?.[0] ?? "0"
  );
  const startTime = OrderMachineContext.useSelector(
    (state) => state.context.startTime
  );
  const endTime = OrderMachineContext.useSelector(
    (state) => state.context.endTime
  );
  const description = OrderMachineContext.useSelector(
    (state) => state.context.description
  );
  const zoom = OrderMachineContext.useSelector((state) => state.context.zoom);

  const uploadsInProgress = useUploadMachineSelector((state) => {
    return state.context.fileUploadActors.length > 0;
  });

  const validationResult: ValidationResult = React.useMemo(() => {
    const survey = sink.surveys?.find((s) => s.id === selectedSurveyId);
    const site = survey?.site_id
      ? sink.sites?.find((site) => site.id === survey.site_id)
      : null;
    const siteLocation = site?.location?.coordinates
      ? {
          longitude: site.location.coordinates[0],
          latitude: site.location.coordinates[1],
        }
      : null;

    return validateOrder({
      imageUploads,
      lidarUploads,
      geometryUploads,
      timezone,
      latitude,
      longitude,
      startTime,
      endTime,
      fileAgeMap,
      siteLocation,
    });
  }, [
    imageUploads,
    lidarUploads,
    geometryUploads,
    timezone,
    latitude,
    longitude,
    startTime,
    endTime,
    fileAgeMap,
    selectedSurveyId,
    sink.surveys,
    sink.sites,
  ]);

  const hasErrors = validationResult.errors.length > 0;
  const hasWarnings = validationResult.warnings.length > 0;
  const canUpload = !hasErrors;

  const isValidDescription = (desc: string) => {
    return desc?.trim().length > 0;
  };

  async function startUpload(files: File[], surveyId: number, orderId: number) {
    const uploadFiles = await Promise.all(
      files.map((file: File) => {
        return {
          data: file,
          key: generateUniqueFileIdentifier(file).name,
          surveyId,
          orderId,
        };
      })
    );

    uploadMachine.send({
      type: "START_UPLOAD",
      files: uploadFiles,
    });
  }

  const uploadedFiles = useUploadMachineSelector((state) =>
    state.context.uploadedFiles.filter((file: string) => {
      const extension = file.split(".").pop()?.toLowerCase();

      return (
        extension !== undefined &&
        extension !== "prj" &&
        extension !== "shx" &&
        extension !== "dbf"
      );
    })
  );

  const allFilesUploaded = React.useMemo(() => {
    const totalUploads =
      lidarUploads.length + geometryUploads.length + imageUploads.length;
    return uploadedFiles.length === totalUploads && !uploadsInProgress;
  }, [
    uploadedFiles.length,
    lidarUploads.length,
    geometryUploads.length,
    imageUploads.length,
    uploadsInProgress,
  ]);

  const buttonLabel = allFilesUploaded
    ? "Submit Order"
    : uploadedFiles.length > 0
    ? "Continue Upload"
    : "Begin Upload";

  const handleUpload = async () => {
    const totalUploads =
      lidarUploads.length + geometryUploads.length + imageUploads.length;

    if (uploadedFiles.length === totalUploads) {
      startUpload([], selectedSurveyId, null);
    }

    const files = [
      ...lidarUploads
        .map((upload) => upload.file)
        .filter((file): file is File => file instanceof File),
      ...geometryUploads.flatMap((upload) =>
        Object.values(upload.files ?? {}).filter(
          (file): file is File => file instanceof File
        )
      ),
      ...imageUploads
        .map((upload) => upload.file)
        .filter((file): file is File => file instanceof File),
    ];

    if (files.length === 0) {
      return;
    }

    const orders = sink.orders.filter(
      (order) =>
        order.survey_id === selectedSurveyId && order.submitted === null
    );

    const order =
      orders.length === 0
        ? (
            await ApiOrders.createOrder(selectedSurveyId, {
              type: "basic",
            })
          ).order
        : orders[0];

    startUpload(files, selectedSurveyId, order.id);
  };

  const handleWarningConfirm = async () => {
    setShowWarningDialog(false);
    await handleUpload();
  };

  if (timezone === null || uploadsInProgress) {
    return null;
  }

  return (
    <div
      css={css`
        padding: 1rem;
        border-top: 1px solid var(--neutral-5);
        background-color: var(--neutral-6);
      `}
    >
      <div
        css={css`
          display: flex;
          flex-direction: column;
        `}
      >
        <FormGroup label="Description (optional)" labelFor="description-input">
          <TextArea
            id="description-input"
            placeholder="Add a description for this order..."
            value={description}
            css={css`
              resize: none;
            `}
            onChange={(e) =>
              orderMachine.send({
                type: "SET_DESCRIPTION",
                description: e.target.value,
              })
            }
            fill
            intent={
              description && !isValidDescription(description)
                ? "danger"
                : "none"
            }
          />
        </FormGroup>
        <div
          css={css`
            display: flex;
            justify-content: space-between;
            align-items: center;
            gap: 1rem;
          `}
        >
          <div
            css={css`
              flex: 1;
            `}
          >
            {hasErrors && (
              <Callout intent="danger" icon="error">
                <div
                  css={css`
                    display: flex;
                    flex-direction: column;
                    gap: 0.5rem;
                  `}
                >
                  There are errors in your upload. Please fix them before
                  continuing.
                </div>
              </Callout>
            )}

            {hasWarnings && !hasErrors && (
              <Callout intent="warning" icon="warning-sign">
                <div
                  css={css`
                    display: flex;
                    flex-direction: column;
                    gap: 0.5rem;
                  `}
                >
                  There are issues with your upload. You can continue with the
                  upload, but these issues may affect processing quality or
                  results.
                  <span
                    css={css`
                      font-size: 0.875rem;
                      color: var(--neutral-2);
                    `}
                  >
                    Click "{buttonLabel}" for a full list of issues.
                  </span>
                </div>
              </Callout>
            )}
          </div>

          <div
            css={css`
              flex-shrink: 0;
            `}
          >
            <Button
              intent={
                hasErrors ? "danger" : hasWarnings ? "warning" : "primary"
              }
              disabled={!canUpload}
              icon={
                hasErrors
                  ? "error"
                  : hasWarnings
                  ? "warning-sign"
                  : "cloud-upload"
              }
              size="large"
              onClick={async () => {
                try {
                  if (hasWarnings) {
                    setShowWarningDialog(true);
                    return;
                  }

                  if (allFilesUploaded) {
                    uploadMachine.send({ type: "SUBMIT_ORDER" });
                    return;
                  } else {
                    await handleUpload();
                  }
                } catch (error) {
                  console.error("Upload failed:", error);
                }
              }}
            >
              {buttonLabel}
            </Button>
          </div>
        </div>
      </div>
      <WarningDialog
        isOpen={showWarningDialog}
        warnings={validationResult.warnings}
        onClose={() => setShowWarningDialog(false)}
        onConfirm={handleWarningConfirm}
      />
    </div>
  );
}
